import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  DestroyRef,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserPackageChangeOptionsConfiguration } from './user-package-change-options-configuration';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { RnOrganizationsProfileVM, RnUsersVM } from '../../../services/rnapi2-service/models/models';
import { LoggedInInfoService } from '../../../services/loggedInInfo/logged-in-info.service';
import { OrganizationLogicService } from '../../../services/organization-logic/organization-logic.service';
import { SelectionService } from '../../../services/selection/selection.service';

@Component({
  selector: 'app-user-package-change-options',
  templateUrl: './user-package-change-options.component.html',
  styleUrls: ['./user-package-change-options.component.scss'],
  standalone: false
})
export class UserPackageChangeOptionsComponent implements OnChanges, OnInit {
  @Input() config?: UserPackageChangeOptionsConfiguration;
  @Output() keepOldPackageOptionSelected = new EventEmitter<boolean>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly orgLogicService = inject(OrganizationLogicService);
  private readonly selectionService = inject(SelectionService);
  private readonly loggedinInfoService = inject(LoggedInInfoService);
  private readonly cdr = inject(ChangeDetectorRef);
  public plural: boolean = false;
  public multiPackages: boolean = false;
  public users: RnUsersVM[];
  public usersText: string;
  public packageToString: string = '';
  public showDeleteUserText: boolean;
  public isPurchasablePackage: string;
  public releaseDisabled: boolean = false;
  public setManually: boolean = false;
  public isWlp: boolean = false;
  public OrgProfile: RnOrganizationsProfileVM;
  public oldPackageActionForm: FormGroup;
  public loading: boolean = false;

  public get f() {
    return this.oldPackageActionForm.controls;
  }

  constructor() {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.subscribeOrganizationProfileSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']?.currentValue) {
      this.updateConfig(changes['config'].currentValue);
    }
  }

  public initializeForm(): void {
    this.oldPackageActionForm = this.formBuilder.group({
      keepOldPackage: ['', Validators.required.bind(Validators)]
    });
    this.oldPackageActionForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.keepOldPackageOptionSelected.emit(this.f.keepOldPackage.value === 'true');
    });
  }

  public updateConfig(config: UserPackageChangeOptionsConfiguration): void {
    if (!config) {
      return;
    }
    this.loading = true;
    this.initializeForm();
    this.showDeleteUserText = config.showDeleteUserText;
    this.isPurchasablePackage = config.isPurchasable;
    this.users = config.users;
    this.plural = this.users?.length > 1;
    this.usersText = this.plural ? this.users?.length.toString() : this.users[0]?.FullName;
    const packageTypes = config.packageTypes;
    this.buildPackageTypeSummary(packageTypes);
    this.multiPackages = config.totalPackages > 1;
    this.isWlp = this.isPurchasablePackage === 'Unpurchasable';
    this.cdr.detectChanges();
  }

  public subscribeOrganizationProfileSelection(): void {
    this.selectionService.SelectedOrgProfile.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (organizationProfile) => {
        if (organizationProfile?.ID !== undefined) {
          this.OrgProfile = organizationProfile;
          const canPurchaseRight = this.loggedinInfoService.loggedInUserHasRights(['PRCHDROPPK']);
          const loggedInUser = this.loggedinInfoService.GetLoggedInUser();
          const isInternal = loggedInUser.IsInternal();
          const loggedInUserOrg = this.loggedinInfoService.GetLoggedInUserOrg();
          const result = this.orgLogicService.CanPurchaseDropPackages(
            canPurchaseRight,
            isInternal,
            this.OrgProfile,
            loggedInUserOrg
          );
          if (!result) {
            this.f.keepOldPackage.setValue('true');
            this.releaseDisabled = true;
            this.setManually = true;
          }
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching selected organization profile:', err);
        this.loading = false;
      }
    });
  }

  public buildPackageTypeSummary(packageTypes: { [key: string]: number[] }): void {
    Object.keys(packageTypes)
      .sort((a, b) => a.localeCompare(b))
      .forEach((p, i) => {
        this.packageToString = this.packageToString.trim();
        if (this.packageToString != '') {
          this.packageToString += ', ';
        }
        this.packageToString = this.packageToString + p;
        if (packageTypes[p].length > 1) {
          const packageLength: string = packageTypes[p].length.toString();
          this.packageToString += ' (' + packageLength + ') ';
        }
      });
  }

  public canSubmit(): boolean {
    return this.oldPackageActionForm.valid && !this.loading && (!this.setManually || (this.setManually && this.releaseDisabled));
  }
}
